import React, { useContext, useEffect, useMemo } from "react"

import { Link, useParams } from "react-router-dom"

import { Heading } from "@chakra-ui/core"
import { ChevronLeftIcon } from "@heroicons/react/24/solid"

import EventEmitter from "src/EventEmitter"

import { gql, useClient, useMutation, useQuery } from "hooks/urql"
import useWebsocket from "hooks/useWebsocket"

import Message from "components/LegacyMessageThreads/Details/Message"
import NotificationContainer from "components/NotificationContainer"
import Layer from "components/Pages/Layer"
import ErrorBoundary from "components/Pages/Teacher/ErrorBoundary"
import TeacherContext from "components/Pages/Teacher/TeacherContext"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import AddTutor from "./AddTutor"
import SendMessage from "./SendMessage"
import ThreadAvatars from "./ThreadAvatars"

const MessageThreadDetails = () => {
  const params: any = useParams()
  const viewer = useContext(TeacherContext)

  const client = useClient()
  const [result, refetch] = useQuery({
    query: messageThreadQuery,
    variables: { id: params.threadId },
    client,
    requestPolicy: "cache-and-network",
  })

  const [, runMutation]: any[] = useMutation(markReadMutation, client)

  const markRead = () => {
    setTimeout(
      () =>
        runMutation({ messageThreadId: params.threadId }).then(() => {
          refetch()
        }),
      2000
    )
  }

  const channelProps = useMemo(() => {
    return { id: viewer.id }
  }, [viewer.id])

  useWebsocket({
    channelName: "MessageUserChannel",
    channelProps,
  })

  useEffect(() => {
    markRead()

    const handleEvent = () => {
      refetch()
      markRead()
    }
    EventEmitter.subscribe("messageReceived", handleEvent)

    return () => {
      EventEmitter.unsubscribe("messageReceived", handleEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layer>
      <Link
        to="/messages"
        className="mb-4 flex max-w-max rounded-md p-2 pl-0 hover:bg-gray-200"
      >
        <ChevronLeftIcon className="h-6 text-gray-300" />
        <span>All Threads</span>
      </Link>

      <ErrorBoundary name="messageThreadDetails">
        <UrqlLoadingIndicator result={result}>
          {({ data: { viewer } }) => (
            <>
              <NotificationContainer />
              <Heading fontSize="3xl" height="16" maxW="50%" isTruncated>
                {viewer.messageThread.title}
              </Heading>

              <div className="mb-4 flex items-start">
                <ThreadAvatars
                  messageThread={viewer.messageThread}
                  viewerId={viewer.id}
                />

                {viewer.id === viewer.messageThread.author.id && (
                  <div className="ml-3 pt-1 pr-1">
                    <AddTutor messageThreadId={params.threadId} />
                  </div>
                )}
              </div>

              <div className="mt-8">
                <Heading fontSize="xl" maxW="50%" isTruncated>
                  Messages
                </Heading>
                <SendMessage threadId={params.threadId} />
              </div>

              {viewer.messageThread.messages.data.map(message => (
                <Message key={message.id} message={message} viewer={viewer} />
              ))}
              {viewer.messageThread.messagesCount >
                viewer.messageThread.messages.data.length && (
                <Link
                  to={`/messages/${params.threadId}/archive`}
                  className="mb-4 max-w-max rounded-md p-2 hover:bg-gray-200"
                >
                  <span>View All Messages</span>
                </Link>
              )}
            </>
          )}
        </UrqlLoadingIndicator>
      </ErrorBoundary>
    </Layer>
  )
}

const messageThreadQuery = gql`
  query MessageThread($id: ID!) {
    viewer {
      id
      type
      messageThread(id: $id) {
        id
        title
        messagesCount
        author {
          id
        }
        users {
          id
          type
          messagingDisplayName
          avatar {
            thumbUrl
          }
        }
        messages {
          data {
            id
            read
            content
            createdAt
            author {
              id
              type
              messagingDisplayName
              avatar {
                thumbUrl
              }
            }
            attachment {
              filename
              contentType
              downloadUrl
              url
              thumbUrl
              previewUrl
            }
          }
        }
      }
    }
  }
`

const markReadMutation = gql`
  mutation MarkRead($messageThreadId: ID!) {
    messageThreadMarkRead(messageThreadId: $messageThreadId) {
      failures {
        message
      }
    }
  }
`

export default MessageThreadDetails
